
import { AllSoiForOrg, CompanyWithSoiForOrg, FundSoiData, SoiData } from "src/app/domain/soi/dto/SoIAuxiliary";
import { Rows } from "../../../interfaces";
import { getHeaders } from "./headers";
import { PortfolioStatus } from "src/app/domain/pp/component/sub/appreciation-at-cost/dtos";
import { formats, getCell, withCellStyles } from "../../../utils";
import { boldGrayBgCenter, boldGrayBgRight, boldLightGrayBgLeft, boldLightGrayBgRight, left, leftBold, leftIntendNoBold, rightBold, rightNoBold } from "src/app/util/excelStyles";
import { generateSumFormula, generateGainLossFormula, generateMoicFormula, nullToUndefined, generateExcelSumFormulaForGrandOrSubTotal, getFundRowIndexesForSoi } from "../../../utils/functions";
import { zeroToUndefined } from "../../fund-performnace-hight-level/mappings";
import { checkIfTextField } from "../soi-by-fund/mappings";
import { CellStyle } from "xlsx-js-style";

export const getPreparedData = (soiList: AllSoiForOrg): Rows => {
  const newData: Rows = [];
  const headers = getHeaders();
  headersForLogic = headers;
  newData.push(headers);
  const soiActive = soiList.active;
  const soiSold = soiList.realized;

  if (soiActive.length) {
    getActiveOrRealizedRows(soiActive, newData, PortfolioStatus.ACTIVE);
  }

  if (soiSold.length) {
    getActiveOrRealizedRows(soiSold, newData, PortfolioStatus.REALIZED);
  }
  if (soiSold.length && soiActive.length) {
    const granTotal = getGrandTotalRow();
    newData.push(...granTotal);
    resetData();
  }

  return newData;
};

const getCompanyRows = (soi: CompanyWithSoiForOrg): Rows => {
  current.status === PortfolioStatus.ACTIVE ? totalCompaniesActiveRowIndexes.push(current.rowIndex) : totalCompaniesSoldRowIndexes.push(current.rowIndex);
  const fundsWithRowIndexes = getFundRowIndexesForSoi(soi, current.rowIndex);
  const mappedToHeaders = getNewCompanyMapping(soi, fundsWithRowIndexes);
  const rows: Rows = [];
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headersForLogic) {
      row.push(withCellStyles(mapped[header], checkIfTextField(leftTextArray, header) ? leftBold : rightBold));
    }
    rows.push(row);
  }
  return rows;
};

const getFundsRows = (soi: FundSoiData): Rows => {
  const mappedToHeaders = getNewFundMapping(soi);
  const rows: Rows = [];
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headersForLogic) {
      row.push(withCellStyles(mapped[header], checkIfTextField(leftTextArray, header) ? leftBold : rightBold));
    }
    rows.push(row);
  }
  return rows;
};

const getRoundsRows = (soiRounds: SoiData[]): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = soiRounds.map((round) => getNewRoundMapping(round));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headersForLogic) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, no-nested-ternary
      row.push(withCellStyles(mapped[header], getStyleForCompanyHeader(leftTextArray, header)
      ));
    }
    rows.push(row);
  }
  return rows;
};

export const getStyleForCompanyHeader = (leftTextArray: string[], header: string): CellStyle => {
  if (checkIfTextField(leftTextArray, header)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return header === 'Company' ? leftIntendNoBold : left;
  }
  return rightNoBold;

};

const getNewRoundMapping = (roundSoi: SoiData): object => {
  const body = {
    Company: getCell({ v: roundSoi.roundName }),
    'Agreement date': roundSoi.agreementDate?.toString(),
    '# of shares': getCell({ v: zeroToUndefined(roundSoi.shares), z: formats.thousandsSeparator, t: 'n' }),
    Investment: getCell({ v: zeroToUndefined(roundSoi.cost), z: formats.thousandsSeparator, t: 'n' }),
    'Fair value': getCell({ v: zeroToUndefined(roundSoi.fmv), z: formats.thousandsSeparator, t: 'n' }),
    'Gain / loss': getCell({
      f: generateGainLossFormula(headersForLogic, 'Investment', 'Fair value', current.rowIndex),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding': getCell({ v: nullToUndefined(roundSoi.holding), z: formats.percentsNoSpace, t: 'n' }),
    MOIC: getCell({
      f: generateMoicFormula(headersForLogic, 'Investment', 'Fair value', current.rowIndex),
      z: formats.multiplier,
      t: 'n',
    }),
    IRR: getCell({ v: nullToUndefined(roundSoi.irr), z: formats.percentsNoSpace, t: 'n' }),
    'Company valuation': getCell({ v: zeroToUndefined(roundSoi.valuation), z: formats.thousandsSeparator, t: 'n' }),
    'empty(1)': null,
    Country: current.country ?? '',
    Industry: current.industry ?? '',
    'Active/Realized': current.status,
  };
  current.rowIndex++;
  return body;
};

const getNewCompanyMapping = (soi: CompanyWithSoiForOrg, totalRow: number[]): [object] => {
  current.country = soi.company.country;
  current.industry = soi.company.industry;
  const body = {
    Company: getCell({ v: soi.company.name }),
    'Agreement date': soi.companyTotal.agreementDate?.toString(),
    '# of shares': getCell({
      f: generateExcelSumFormulaForGrandOrSubTotal('# of shares', headersForLogic, totalRow),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Investment: getCell({
      f: generateExcelSumFormulaForGrandOrSubTotal('Investment', headersForLogic, totalRow),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: generateExcelSumFormulaForGrandOrSubTotal('Fair value', headersForLogic, totalRow),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain / loss': getCell({
      f: generateGainLossFormula(headersForLogic, 'Investment', 'Fair value', current.rowIndex),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding': getCell({
      v: nullToUndefined(soi.companyTotal.holding),
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    MOIC: getCell({
      f: generateMoicFormula(headersForLogic, 'Investment', 'Fair value', current.rowIndex),
      z: formats.multiplier,
      t: 'n',
    }),
    IRR: getCell({ v: nullToUndefined(soi.companyTotal.irr), z: formats.percentsNoSpace, t: 'n' }),
    'Company valuation': getCell({ v: zeroToUndefined(soi.companyTotal.valuation), z: formats.thousandsSeparator, t: 'n' }),
    'empty(1)': null,
    Country: soi.company.country,
    Industry: soi.company.industry,
    'Active/Realized': current.status,
  };
  current.rowIndex++;
  return [body];
};

const getNewFundMapping = (soi: FundSoiData): [object] => {
  const roundLen = soi.rounds.length;
  const body = {
    Company: getCell({ v: soi.fundInfo.name }),
    'Agreement date': soi.fundTotal.agreementDate?.toString(),
    '# of shares': getCell({
      f: generateSumFormula(headersForLogic, '# of shares', current.rowIndex, roundLen),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Investment: getCell({
      f: generateSumFormula(headersForLogic, 'Investment', current.rowIndex, roundLen),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: generateSumFormula(headersForLogic, 'Fair value', current.rowIndex, roundLen),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain / loss': getCell({
      f: generateGainLossFormula(headersForLogic, 'Investment', 'Fair value', current.rowIndex),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding': getCell({
      v: nullToUndefined(soi.fundTotal.holding),
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    MOIC: getCell({
      f: generateMoicFormula(headersForLogic, 'Investment', 'Fair value', current.rowIndex),
      z: formats.multiplier,
      t: 'n',
    }),
    IRR: getCell({ v: nullToUndefined(soi.fundTotal.irr), z: formats.percentsNoSpace, t: 'n' }),
    'Company valuation': getCell({ v: zeroToUndefined(soi.fundTotal.valuation), z: formats.thousandsSeparator, t: 'n' }),
    'empty(1)': null,
    Country: current.country ?? '',
    Industry: current.industry ?? '',
    'Active/Realized': current.status,
  };
  current.rowIndex++;
  return [body];
};

const getSubTotalRow = (status: string): Rows => {
  const subtotalMapped = getNewSubTotalMapping(status);
  const rows: Rows = [];
  for (const mapped of subtotalMapped) {
    const row = [];
    for (const header of headersForLogic) {
      row.push(withCellStyles(mapped[header], checkIfTextField(leftTextArray, header) ? boldLightGrayBgLeft : boldLightGrayBgRight));
    }
    rows.push(row);
  }
  return rows;
};

const getNewSubTotalMapping = (status: string): [object] => {
  const totalRow = status === 'Active' ? totalCompaniesActiveRowIndexes : totalCompaniesSoldRowIndexes;
  const body = {
    Company: status + ' portfolio investment',
    'Agreement date': '',
    '# of shares': getCell({
      f: generateExcelSumFormulaForGrandOrSubTotal('# of shares', headersForLogic, totalRow),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Investment: getCell({
      f: generateExcelSumFormulaForGrandOrSubTotal('Investment', headersForLogic, totalRow),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: generateExcelSumFormulaForGrandOrSubTotal('Fair value', headersForLogic, totalRow),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain / loss': getCell({
      f: generateGainLossFormula(headersForLogic, 'Investment', 'Fair value', current.rowIndex),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding': getCell({
      v: '',
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    MOIC: getCell({
      f: generateMoicFormula(headersForLogic, 'Investment', 'Fair value', current.rowIndex),
      z: formats.multiplier,
      t: 'n',
    }),
    IRR: getCell({
      v: '',
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    'Company valuation': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Country: '',
    Industry: '',
    'Active/Realized': '',
  };
  return [body];
};

export const getGrandTotalRow = (): Rows => {
  const mappedToHeaders = getNewTotalMapping();
  const rows: Rows = [];
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headersForLogic) {
      if (header === 'Company') {
        row.push(withCellStyles(mapped[header], boldGrayBgCenter));
      } else {
        row.push(withCellStyles(mapped[header], boldGrayBgRight));
      }
    }
    rows.push(row);
  }
  return rows;
};

const getNewTotalMapping = (): [object] => {
  const totalIndexs = totalCompaniesActiveRowIndexes.concat(totalCompaniesSoldRowIndexes);
  const body = {
    Company: 'Grand total',
    'Agreement date': '',
    '# of shares': getCell({
      f: generateExcelSumFormulaForGrandOrSubTotal('# of shares', headersForLogic, totalIndexs),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Investment: getCell({
      f: generateExcelSumFormulaForGrandOrSubTotal('Investment', headersForLogic, totalIndexs),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: generateExcelSumFormulaForGrandOrSubTotal('Fair value', headersForLogic, totalIndexs),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain / loss': getCell({
      f: generateGainLossFormula(headersForLogic, 'Investment', 'Fair value', current.rowIndex),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding': getCell({
      v: '',
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    MOIC: getCell({
      f: generateMoicFormula(headersForLogic, 'Investment', 'Fair value', current.rowIndex),
      z: formats.multiplier,
      t: 'n',
    }),
    IRR: getCell({
      v: '',
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    'Company valuation': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Country: '',
    Industry: '',
    'Active/Realized': '',
  };
  return [body];
};

const getActiveOrRealizedRows = (soiArray: CompanyWithSoiForOrg[], newData: Rows, status: string): void => {
  const activeRealizedTypeRow = [];
  current.status = status;
  current.rowIndex++;
  activeRealizedTypeRow.push(withCellStyles(current.status, leftBold));
  newData.push(activeRealizedTypeRow);
  soiArray.forEach((soi) => {
    newData.push(...getCompanyRows(soi));
    soi.fundsSoi.forEach((f) => {
      newData.push(...getFundsRows(f));
      newData.push(...getRoundsRows(f.rounds));
    });
  });
  const subTotalActiveRealized = getSubTotalRow(current.status);
  newData.push(...subTotalActiveRealized);
  current.rowIndex++;
};

const resetData = (): void => {
  current.rowIndex = 2;
  current.status = null;
  current.country = '';
  current.industry = '';
  totalCompaniesActiveRowIndexes = [];
  totalCompaniesSoldRowIndexes = [];
  headersForLogic = [];
};

let headersForLogic = [];

const current = {
  rowIndex: 2,
  status: null,
  country: '',
  industry: '',
};

let totalCompaniesActiveRowIndexes: number[] = [];
let totalCompaniesSoldRowIndexes: number[] = [];
const leftTextArray = ['Company', 'Industry', 'Country'];
